// The blocking reads a new-session action resolves before deciding whether the
// session-create surface is blocked. Concentrated here, in one hook, so the
// decision hook (useNewSessionAction) and every consumer stay store-clean and
// remain movable into a workspace package. When the timer slice is dismantled
// this file's internals re-point; its signature does not.
import { offlineActiveRecordingIdAtom } from '@/atoms/offline-recording';
import {
  hasIntegrationAtom,
  integrationDataAtom,
} from '@heidi/integrations/ehr/data/atoms';
// eslint-disable-next-line @heidi/rules/no-new-rematch-coupling -- legacy Rematch read concentrated in one hook so the decision hook and consumers hold no store coupling; dies with the timer slice
import { type RootState } from '@/store';
import { useAtomValue } from 'jotai';
// eslint-disable-next-line @heidi/rules/no-new-rematch-coupling -- legacy Rematch read concentrated in one hook so the decision hook and consumers hold no store coupling; dies with the timer slice
import { useSelector } from 'react-redux';

type NewSessionBlockers = {
  /** A recording timer is running — starting another session is blocked. */
  isRecordingTimerRunning: boolean;
  /**
   * An offline capture is live or paused. It does not run the timer above, and
   * `useNewSession` refuses the create itself; this greys the control out.
   */
  isOfflineCaptureActive: boolean;
  /**
   * Partner-integration sessions are locked to the launched patient; starting
   * one with no patient selected is invalid.
   */
  isIntegrationPatientLocked: boolean;
};

export function useNewSessionBlockers(): NewSessionBlockers {
  const isRecordingTimerRunning = useSelector(
    (state: RootState) => state.timerModel.isRunning
  );
  const hasIntegration = useAtomValue(hasIntegrationAtom);
  const integrationData = useAtomValue(integrationDataAtom);
  const offlineActiveRecordingId = useAtomValue(offlineActiveRecordingIdAtom);

  return {
    isRecordingTimerRunning,
    isOfflineCaptureActive: offlineActiveRecordingId !== null,
    isIntegrationPatientLocked:
      hasIntegration &&
      (!integrationData.patientId || integrationData.patientId === '""'),
  };
}
